import { useCallback, useState, useEffect } from 'react';

import TrackingHeader from './TrackingHeader';

import Notes from './Notes';
import ShippingCost from './ShippingCost/ShippingCost';
import ShippingDate from './ShippingDate';
import ShippingInformation from './ShippingInformation';
import TrackingModalUpdate from './TrackingModalUpdate';
import { useForm } from 'react-hook-form';
import {
  convertDateToDateTimeLocalFormat,
  isUrl,
  isValidShippingCost,
} from '../utils';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CarriersService } from 'src/services/Carriers';
import { InvalidShippingCostModal } from './InvalidShippingCostModal';

const Tracking = ({
  rastreioRef,
  shipping,
  orderUUID,
  creationDate,
  shippingCostValidationEnabled,
  fetchOrderDetail,
  orderNumber,
  subtotal,
  canEditTracking,
  orderSource,
}) => {
  const [carrierList, setCarrierList] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [alertShippingModalOpen, setAlertShippingModalOpen] = useState(false);
  const [trackingPayload, setTrackingPayload] = useState({});

  const fetchCarriers = useCallback(async () => {
    try {
      const response = await CarriersService.getCarriers();
      setCarrierList(response.couriers);
    } catch (error) {
      console.warn('Erro ao buscar transportadoras', error);
      setCarrierList([]);
    }
  }, []);

  useEffect(() => {
    fetchCarriers();
  }, []);

  const schema = yup.object({
    finalShippingCost: yup
      .number()
      .min(0, 'Insira um valor válido')
      .notRequired()
      .transform(value => (value ? Number(value) : undefined)),
    courierUUID: yup
      .string()
      .required('Selecione uma transportadora')
      .nullable(),
    trackingCode: yup
      .string()
      .optional()
      .test(
        'not-url',
        'O código de rastreio não pode ser uma URL',
        value => !isUrl(value),
      ),
    trackingUrl: yup
      .string()
      .required('Insira uma URL de rastreamento')
      .url(
        'Digite uma URL válida , exemplo: https://www.meusite.com.br/rastreio (deve começar com http:// ou https://)',
      ),
    shippingDate: yup
      .date('Insira uma data válida')
      .max(new Date(), 'Data inválida, insira uma data anterior a atual')
      .min(
        new Date(creationDate),
        'Data inválida, insira uma data posterior a data de criação do pedido',
      )
      .typeError('Insira uma data válida')
      .required('Campo obrigatório'),
  });

  const defaultFinalShippingCost = () => {
    if (shipping.finalShippingCost) return shipping.finalShippingCost;
    if (shipping.requiresShippingCost) return undefined;
    return 0;
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      courierUUID: shipping.courierUUID,
      trackingCode: shipping.trackingCode,
      trackingUrl: shipping.trackingUrl,
      finalShippingCost: defaultFinalShippingCost(),
      shippingDate: convertDateToDateTimeLocalFormat(shipping.shippingDate),
      trackingInstructions: shipping.trackingInstructions,
    },
    resolver: yupResolver(schema),
  });

  const watchNotesText = watch('trackingInstructions');

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const onSubmit = data => {
    const isValidShipping = isValidShippingCost(
      data.finalShippingCost,
      subtotal,
    );

    if (
      shippingCostValidationEnabled &&
      data.finalShippingCost !== defaultFinalShippingCost() &&
      !isValidShipping
    ) {
      setAlertShippingModalOpen(true);
      return;
    }

    const shippingCompanyName = carrierList.find(
      carrier => carrier.uuid === data.courierUUID,
    )?.alias;

    setTrackingPayload({
      ...data,
      shippingDate: new Date(data.shippingDate).toISOString(),
      shippingCompanyName,
    });
    handleOpenModal();
  };

  return (
    <>
      <TrackingHeader rastreioRef={rastreioRef} />
      <form onSubmit={handleSubmit(onSubmit)}>
        {carrierList && (
          <ShippingInformation
            register={register}
            carrierList={carrierList}
            canEditTracking={canEditTracking}
            errors={errors}
            control={control}
          />
        )}
        <ShippingCost
          requiresShippingCost={shipping.requiresShippingCost}
          control={control}
          errors={errors}
          canEditTracking={canEditTracking && shipping.requiresShippingCost}
          orderSource={orderSource}
        />

        <ShippingDate
          register={register}
          errors={errors}
          canEditTracking={canEditTracking}
          minDate={creationDate}
        />
        <Notes
          register={register}
          notesTextLength={watchNotesText?.length || 0}
          canEditTracking={canEditTracking}
        />
        <div className="row d-flex align-items-center justify-content-end">
          <div className="col-12 offset-md-4 col-md-4 d-flex justify-content-end">
            <button
              className={`input-add mt-2 salvar-input ${
                !canEditTracking && 'disabled'
              }`}
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!canEditTracking}
            >
              Salvar
            </button>
          </div>
        </div>
        <div className="row d-flex align-items-center mb-4 text-right">
          <p className="mt-2 text-secondary">
            O pedido terá seu status atualizado para “Enviado” após salvar as
            informações.
          </p>
        </div>
      </form>

      {openModal && (
        <TrackingModalUpdate
          trackingPayload={trackingPayload}
          setTrackingPayload={setTrackingPayload}
          orderUUID={orderUUID}
          closeModal={handleCloseModal}
          fetchOrderDetail={fetchOrderDetail}
        />
      )}
      {alertShippingModalOpen && (
        <InvalidShippingCostModal
          closeModal={() => setAlertShippingModalOpen(false)}
          orderNumber={orderNumber}
        />
      )}
    </>
  );
};

export default Tracking;
